import Alpine from 'alpinejs'
import intersect from '@alpinejs/intersect'
import * as inputValidations from '../cardmatch/app/services/input-validations'
import { getUserData } from '../cardmatch/app/services/auth-api'
import { getCookieByName } from '../../bankrate-utilities'
import qsa from '../helpers/qsa'
import makeAJAXRequest from '../helpers/makeAJAXRequest'
import getCorrelationId from '../helpers/getCorrelationId'

import {
  formViewed,
  formStarted,
  formContinued,
  formSubmitted,
  fieldInputted,
  coreIdentify,
  loaderViewed,
  errorModalViewed,
  errorModalRestartClicked,
  closeModalClicked
} from './tagular'

Alpine.plugin(intersect)

// If we need to call Alpine elsewhere (optional).
window.Alpine = Alpine

Alpine.data('appOddsForm', () => ({
  form: {
    step: 1,
    extendTerms: false,
    isLoggedIn: false,
    isLoading: false,
    hasError: false,
    showGMapsResults: false,
    addressPredictions: [],
    focusedAddressPrediction: -1,
    inputs: {
      firstName: {
        name: 'firstName',
        value: '',
        valid: false,
        hasError: false,
        errorMsg: '',
        submissionKey: 'first_name',
        tagular: {
          fieldLabel: 'First name',
          fieldName: 'First Name',
          fieldType: 'First Name'
        }
      },
      lastName: {
        name: 'lastName',
        value: '',
        valid: false,
        hasError: false,
        errorMsg: '',
        submissionKey: 'last_name',
        tagular: {
          fieldLabel: 'Last name',
          fieldName: 'Last Name',
          fieldType: 'Last Name'
        }
      },
      streetAddress: {
        name: 'streetAddress',
        value: '',
        valid: false,
        hasError: false,
        errorMsg: '',
        pullGoogleResults: false,
        submissionKey: 'address',
        tagular: {
          fieldLabel: 'Street Address',
          fieldName: 'StreetAddress',
          fieldType: 'Street Address'
        }
      },
      city: {
        name: 'city',
        value: '',
        valid: false,
        hasError: false,
        errorMsg: '',
        submissionKey: 'city',
        tagular: {
          fieldLabel: 'City',
          fieldName: 'City',
          fieldType: 'City'
        }
      },
      state: {
        name: 'state',
        value: '',
        valid: false,
        hasError: false,
        errorMsg: '',
        submissionKey: 'state',
        tagular: {
          fieldLabel: 'State',
          fieldName: 'State',
          fieldType: 'State'
        }
      },
      zipCode: {
        name: 'zipCode',
        value: '',
        valid: false,
        hasError: false,
        errorMsg: '',
        submissionKey: 'zip',
        tagular: {
          fieldLabel: 'Zipcode',
          fieldName: 'Zipcode',
          fieldType: 'Zipcode'
        }
      },
      ssn: {
        name: 'ssn',
        value: '',
        valid: false,
        hasError: false,
        errorMsg: '',
        submissionKey: 'ssn',
        tagular: {
          fieldLabel: 'SSN',
          fieldName: 'SSN',
          fieldType: 'SSN'
        }
      },
      emailAddress: {
        name: 'emailAddress',
        value: '',
        valid: false,
        hasError: false,
        errorMsg: '',
        submissionKey: 'email',
        tagular: {
          fieldLabel: 'Email',
          fieldName: 'Email',
          fieldType: 'Email'
        }
      }
    }
  },

  nextStep() {
    this.form.step += 1
  },

  prevStep() {
    this.form.step -= 1
  },

  toggleTerms() {
    this.form.extendTerms = !this.form.extendTerms
  },

  validateInput(input) {
    let validation = null
    let valueLower = ''
    // Determine and validate input.
    switch (input.name) {
      case 'firstName':
      case 'lastName':
        validation = inputValidations.nameValidation(input.value)
        break
      case 'city':
        validation = inputValidations.cityValidation(input.value)
        break
      case 'state':
        validation = inputValidations.stateValidation(input.value)
        break
      case 'zipCode':
        validation = inputValidations.zipCodeValidation(input.value)
        break
      case 'emailAddress':
        valueLower = input.value.toLowerCase()
        validation = inputValidations.emailValidation(valueLower)
        break
      case 'ssn':
        validation = inputValidations.ssnValidation(input.value)
        break
      case 'streetAddress':
        validation = inputValidations.defaultValidation(input.value)
        break
    }

    // Update data associated to input.
    this.form.inputs[input.name].valid = validation.isValidated
    this.form.inputs[input.name].hasError = !validation.isValidated
    this.form.inputs[input.name].errorMsg = validation.message || ''
  },

  validateStep(inputNames, stepName, submit = false) {
    if (!inputNames) {
      return false
    }
    inputNames = inputNames.split(', ')
    const isValid = inputNames.every(inputName => {
      const input = this.form.inputs[inputName]
      // Re-validate inputs
      this.validateInput(input)

      return input.valid === true
    })

    // If all step inputs valid proceed to next step otherwise nothing.
    if (isValid && stepName) {
      formContinued(stepName, this.form.step)
      this.nextStep()
    } else if (isValid && submit) {
      // Form Submitted Tagular
      // Submit the form if last step has valid inputs.
      this.$nextTick(() => {
        this.submitForm()
        // Submit tagular events.
        formSubmitted()
        coreIdentify(this.form.inputs.emailAddress.value)
      })
    } else {
      return
    }
  },

  initGoogleMapsService() {
    // eslint-disable-next-line no-undef
    this.googleMapsService = new google.maps.places.AutocompleteService()
    const streetInput = document.querySelector('#streetAddress')
    // eslint-disable-next-line no-undef
    this.placesService = new google.maps.places.PlacesService(streetInput)
    // eslint-disable-next-line no-undef
    this.geocoder = new google.maps.Geocoder()
    // eslint-disable-next-line no-undef
    this.sessionToken = new google.maps.places.AutocompleteSessionToken()
  },

  getAddressPredictions(value) {
    try {
      this.googleMapsService.getPlacePredictions(
        { input: value, types: ['address'], sessionToken: this.sessionToken },
        (predictions, status) => {
          // eslint-disable-next-line no-undef
          if (status !== google.maps.places.PlacesServiceStatus.OK) return
          this.form.showGMapsResults = true
          this.form.addressPredictions = predictions
          // Reset the focused prediction index.
          this.form.focusedAddressPrediction = -1
        }
      )
    } catch (error) {
      // Log error to Rollbar.
      if (window.Rollbar) {
        window.Rollbar.error('Approval Odds - Google Maps Places Error', error)
      }
      // Hide results.
      this.$nextTick(() => {
        this.form.showGMapsResults = false
      })
    }
  },

  // Move up or down once in gmaps predictions.
  traversePredictions(value = 1) {
    // value is either 1 or -1
    const next = this.form.focusedAddressPrediction + value
    if (next >= 0 && next < this.form.addressPredictions.length) {
      this.form.focusedAddressPrediction = next
    } else if (next === -1) {
      this.form.focusedAddressPrediction =
        this.form.addressPredictions.length - 1
    } else if (next === this.form.addressPredictions.length) {
      this.form.focusedAddressPrediction = 0
    }
  },

  handlePredictionSelection(index) {
    // Determine index to grab depending on if index param passed to function call.
    const perdictionIndex =
      index !== undefined ? index : this.form.focusedAddressPrediction
    // Grab the selected google places address.
    const selected = this.form.addressPredictions[perdictionIndex]
    // Hide the google results list.
    this.form.showGMapsResults = false

    // Get the address details
    this.selectAddress(selected.place_id)
    // Make sure the google results list is hidden / update to original state.
    this.form.showGMapsResults = false
    this.form.addressPredictions = []
    this.form.focusedAddressPrediction = -1
    this.form.inputs.streetAddress.pullGoogleResults = false
  },

  selectAddress(placeId) {
    // Fill in Address inputs
    const details = {
      placeId,
      sessionToken: this.sessionToken,
      fields: ['address_component']
    }
    this.placesService.getDetails(details, this.parseAddress.bind(this))
  },

  parseAddress(address) {
    const parsedAddress = {
      street_number: '',
      route: '',
      full_address: '',
      city: '',
      state: '',
      zipcode: ''
    }

    address.address_components.forEach(component => {
      switch (component.types[0]) {
        case 'street_number':
          parsedAddress.street_number = component.long_name
          break
        case 'route':
          parsedAddress.route = component.long_name
          break
        case 'locality':
          parsedAddress.city = component.long_name
          break
        case 'administrative_area_level_1':
          parsedAddress.state = component.short_name
          break
        case 'postal_code':
          parsedAddress.zipcode = component.short_name
          break
        default:
          break
      }
    })
    if (parsedAddress.street_number && parsedAddress.route) {
      parsedAddress.full_address = `${parsedAddress.street_number} ${parsedAddress.route}`
    }
    // Update the inputs values.
    this.updateAddressInputs(parsedAddress)
  },

  // Update the form address inputs values.
  updateAddressInputs(address) {
    const inputs = this.form.inputs
    inputs.streetAddress.value = address.full_address || address.route
    inputs.city.value = address.city
    inputs.state.value = address.state
    inputs.zipCode.value = address.zipcode
  },

  // This fixes an issue when you use browser history navigation (ie back).
  // Clears pre-filled inputs by browser which our out of sync with Alpine state.
  clearAllInputs() {
    const formInputs = qsa('.ao-form input')
    formInputs.forEach(input => {
      input.value = ''
    })
  },

  async handleLoggedInUser() {
    // Determine which cookie to look for.
    const COOKIE_NAME = window.env === 'prod' ? 'BAEAU' : 'BAEAU-QA'
    const BAEAU_USER_COOKIE = getCookieByName(COOKIE_NAME)
    // If no cookie exists then user is not logged in.
    if (!BAEAU_USER_COOKIE) {
      return
    }
    // Set cookie value as auth token to grab user data.
    const authData = {
      token: BAEAU_USER_COOKIE
    }
    const userData = await getUserData({ authData, fake: false })
    // If a user is returned then update form input data.
    if (userData) {
      const inputs = this.form.inputs
      Object.keys(userData).forEach(field => {
        if (inputs[field]) {
          inputs[field].value = userData[field] || ''
        }
      })
    }
  },

  restartForm() {
    this.form.isLoading = false
    this.form.hasError = false
    this.form.step = 1
    errorModalRestartClicked()
  },

  // Tagular events.
  formViewed,
  fieldInputted,
  loaderViewed,
  errorModalViewed,

  // Only using this tagular event on error modal for now.
  closeModalTagular() {
    if (this.form.hasError) {
      closeModalClicked()
    }
  },

  generateRequestData() {
    const requestData = {
      applicant: {},
      meta: {
        anonymous_id: window._Cohesion.anonymousId,
        correlation_id: getCorrelationId(),
        instance_id: window._Cohesion.instanceId,
        session_id: window._Cohesion.sessionId,
        tenant_id: window._Cohesion.tenantId,
        write_key: window._Cohesion.writeKey
      },
      terms: {
        body:
          'By clicking “Agree and See Results” you acknowledge receipt of our Privacy Notice, Privacy Policy and agree to our Terms of Use. By agreeing, you are giving your written instruction to Bankrate and our lending partners (together, “Us”) to obtain a soft pull of your credit report to determine whether you may be eligible for certain targeted offers, including pre-qualified and pre-approved offers (your "CardMatch offers"), as well as display what we estimate your approval odds to be for participating offers (“Approval Odds”). You instruct Us to do this each time you return to our sites to view product offerings and up to once per month so you can be provided up-to-date results. You understand that this is not an application for credit and CardMatch offers and Approval Odds do not guarantee you will be approved for a partner offer. To apply for a product you will need to submit an application directly with that provider. Seeing your results won\'t hurt your credit score. Applying for a product may impact your score. See partner for complete product terms.',
        tcpa_terms: 'false'
      }
    }

    // Add applicant information to requestData.
    Object.values(this.form.inputs).forEach(value => {
      requestData.applicant[value.submissionKey] = value.value
    })

    return requestData || {}
  },

  // Re-maps the approval odds results from array into object for easy look up.
  reformatApprovalOdds(odds) {
    return odds.reduce((accumulator, odd) => {
      accumulator[odd.productId] = odd.approvalOdds
      return accumulator
    })
  },

  makeSubmissionRequest(endpoint, requestData) {
    makeAJAXRequest('POST', endpoint, requestData)
      .then(result => {
        if (result) {
          if (result.data && result.data.approvalOdds) {
            // Re-map approval odds results into an object.
            result.data.approvalOdds = this.reformatApprovalOdds(
              result.data.approvalOdds
            )
            // Save results to session storage.
            const sessionResults = {
              data: result.data
            }
            sessionStorage.setItem(
              'approvalOdds',
              JSON.stringify(sessionResults)
            )
            this.$nextTick(() => {
              // Hide AO form modal.
              document
                .querySelector('#ao-form-modal')
                .classList.remove('--open')
              // Scroll to top of cards.
              document.querySelector('#cards-wrapper').scrollIntoView()
              // Reset form state.
              this.form.isLoading = false
              this.form.hasError = false
            })
          } else {
            // Handle bad result.
            this.$nextTick(() => {
              this.form.isLoading = false
              this.form.hasError = true
            })
          }
        }
      })
      .catch(error => {
        // Handle error
        this.form.isLoading = false
        this.form.hasError = true
        console.error(error)
      })
  },

  submitForm() {
    // Show loading modal.
    this.form.isLoading = true
    // Grab request data.
    const requestData = this.generateRequestData()

    // Determine which endpoint needs to be used based on environment.
    let aoEndpoint =
      window.env === 'prod'
        ? 'https://www.bankrate.com/api/credit-cards/approval-odds/?api_token=3|av9JM954AvhEc7JnIsYUlgWYSn42155EslweYeEC'
        : 'https://qa.bankrate.com/api/credit-cards/approval-odds/?api_token=1|YPGwXBR8qbAU1cxzpGlTLFJLveIvq3KQbi8KiBOu'

    this.makeSubmissionRequest(aoEndpoint, requestData)
  },

  init() {
    this.$nextTick(() => {
      this.initGoogleMapsService()
      this.clearAllInputs()
      this.handleLoggedInUser()
    })
    this.$watch('form.inputs.streetAddress.value', value => {
      if (this.form.inputs.streetAddress.pullGoogleResults) {
        this.getAddressPredictions(value)
      }
    })
    this.$watch('form.inputs.firstName.value', () => {
      if (!this.form.userStarted) {
        formStarted()
        this.form.userStarted = true
      }
    })
  }
}))

Alpine.start()
